import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get, isEmpty, isNil, isEqual } from 'lodash';

import { requestWithAuth } from 'helpers/saga-helpers';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import userToken from 'containers/services/security/access-token-storage';
import { makeSelectKeycloakToken } from 'containers/AppContainer/selectors';

export type ServiceStatus = {
  name: string;
  status: string;
  color: string;
};

export const useHealthCheck = (refreshInterval = 10000) => {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [serverDown, setServerDown] = useState(false);

  const intervenantToken = useSelector(makeSelectKeycloakToken());

  const sagisToken = get(userToken.get(), 'token', null);

  const token = !isNil(sagisToken) ? sagisToken : intervenantToken;

  const feature = featureService.hasFeature(FEATURES.HEALTH_CHECK_POLLING);

  const fetchHealth = useCallback(async () => {
    if (isNil(token)) {
      setServerDown(true);
      setLoading(false);
      return;
    }
    const url = !isNil(sagisToken)
      ? '/corporeal/check-health'
      : '/corporeal/check-health?isIntervenant=true';

    try {
      const response = !isNil(token)
        ? await requestWithAuth(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          })
        : {};

      const newServices: ServiceStatus[] = get(response, 'data', []);

      setServices(prevServices => {
        if (isEqual(prevServices, newServices)) {
          return prevServices;
        }

        return newServices;
      });

      const nextServerDown =
        isEmpty(newServices) ||
        newServices.some((service: ServiceStatus) => service.color === 'red');

      setServerDown(prev => (prev === nextServerDown ? prev : nextServerDown));
    } catch (error) {
      setServerDown(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!feature) {
      return;
    }

    fetchHealth();

    const interval = setInterval(() => {
      fetchHealth();
    }, refreshInterval);

    return () => {
      clearInterval(interval);
    };
  }, [fetchHealth, refreshInterval, feature]);

  return {
    services,
    loading,
    serverDown,
    refresh: fetchHealth,
  };
};
